/**
 * Meta Pixel for the app (app.conistra.ai).
 *
 * This exists because Meta buys more of whatever event you feed it. Until now
 * the only signal it received was a click on a signup CTA on the marketing site
 * — so the ad budget was optimising for people who click buttons, not people who
 * pay. The registration and the payment both happen on this host, and Meta could
 * not see either of them.
 *
 * ── Three decisions worth knowing before you change this ───────────────────
 *
 *  1. The pixel is EVENT-SCOPED, not app-wide. There is no `initMetaPixel()` in
 *     `index.tsx`, and that is deliberate: it boots only on the surfaces where a
 *     conversion is reported (the /signup funnel, /activate, and a successful
 *     in-app upgrade). Loading it globally would stream the in-product browsing
 *     of logged-in, paying customers to an ad network that can do nothing useful
 *     with it — page weight and privacy exposure for zero acquisition value.
 *     Adding a new conversion? Call `metaTrack` from it; it boots on demand.
 *
 *  2. Cross-subdomain attribution comes for free. The pixel writes `_fbp`/`_fbc`
 *     on the registrable domain (`.conistra.ai`), so an ad click that lands on
 *     www.conistra.ai is still attributable when the Purchase fires here. This
 *     mirrors what `cross_subdomain_cookie` does for PostHog — but note the two
 *     are independent mechanisms; breaking one does not warn you about the other.
 *
 *  3. `Purchase` is the ONLY event that may carry revenue. Meta sums `value`
 *     across value-carrying events for ROAS, so firing `Subscribe` alongside
 *     `Purchase` for one payment would silently report double the revenue and
 *     make every spend decision downstream of it wrong.
 *
 * PostHog remains the source of truth for analysis — every call here shadows an
 * existing `app_*` capture. This file only translates that funnel into the nine
 * standard events Meta's optimiser actually understands.
 */

const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || '1798226471522794';

type FbqParams = Record<string, unknown>;

interface Fbq {
  (_method: string, _eventOrId: string, _params?: FbqParams): void;
  callMethod?: (..._args: unknown[]) => void;
  queue: unknown[];
  push: unknown;
  loaded: boolean;
  version: string;
}

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

let booted = false;

/**
 * Meta's standard loader. Builds the fbq() stub and its command queue
 * synchronously, then pulls fbevents.js async — so events fired in the same tick
 * as the boot are queued and replayed, not lost.
 */
function loadPixelScript(): void {
  if (window.fbq) return;

  const stub = function (...args: unknown[]) {
    if (stub.callMethod) {
      stub.callMethod.apply(stub, args);
    } else {
      stub.queue.push(args);
    }
  } as unknown as Fbq;

  stub.queue = [];
  stub.push = stub;
  stub.loaded = true;
  stub.version = '2.0';

  window.fbq = stub;
  if (!window._fbq) window._fbq = stub;

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  const first = document.getElementsByTagName('script')[0];
  if (first && first.parentNode) {
    first.parentNode.insertBefore(script, first);
  } else {
    document.head.appendChild(script);
  }
}

/**
 * Boot the pixel and fire its PageView. Idempotent, and safe to call from a
 * render path. Call it explicitly at the top of an acquisition funnel so the
 * pixel is warm before the conversion lands; otherwise `metaTrack` handles it.
 */
export function initMetaPixel(): void {
  if (booted || !PIXEL_ID || typeof window === 'undefined') return;
  booted = true;
  try {
    loadPixelScript();
    window.fbq?.('init', PIXEL_ID);
    window.fbq?.('track', 'PageView');
  } catch {
    /* A marketing tag must never be able to break the signup flow. */
  }
}

/**
 * Report a standard event, booting the pixel first if this is the first one.
 * Never throws: a meaningful share of visitors block connect.facebook.net, and
 * the code paths calling this are the ones that earn the money.
 */
export function metaTrack(event: string, params?: FbqParams): void {
  try {
    initMetaPixel();
    window.fbq?.('track', event, cleanParams(params));
  } catch {
    /* see above */
  }
}

/** Meta silently drops events with undefined values in the params object. */
function cleanParams(params?: FbqParams): FbqParams {
  const out: FbqParams = {};
  if (!params) return out;
  Object.keys(params).forEach((key) => {
    if (params[key] !== undefined && params[key] !== null) out[key] = params[key];
  });
  return out;
}

/**
 * Revenue in the major units Meta expects (12.00, not 1200), or undefined when
 * the plan is free or unknown. Returning undefined rather than 0 is load-bearing:
 * a stream of zero-value Purchases would drag any value-optimised campaign
 * towards the people least likely to pay.
 */
export function planValue(priceCents?: number): number | undefined {
  if (typeof priceCents !== 'number' || priceCents <= 0) return undefined;
  return Number((priceCents / 100).toFixed(2));
}

/** Meta wants an uppercase ISO-4217 code; our plans store it lowercase. */
export function planCurrency(currency?: string): string | undefined {
  return currency ? currency.toUpperCase() : undefined;
}

/**
 * Fire a callback at most once per key, for good, across reloads.
 *
 * /activate is reached by redirect from Stripe and is retried by its own polling
 * loop, so its "subscription is live" branch runs more than once for a single
 * payment. PostHog tolerates that; Meta does not — duplicate Purchases inflate
 * both the conversion count the optimiser learns from and the revenue every
 * spend decision is judged against. localStorage rather than a ref because the
 * duplicates arrive on fresh page loads.
 */
export function onceEver(key: string, fn: () => void): void {
  const storageKey = `conistra_meta_once_${key}`;
  try {
    if (window.localStorage.getItem(storageKey)) return;
    window.localStorage.setItem(storageKey, String(Date.now()));
  } catch {
    // Private mode / storage disabled. Reporting a possible duplicate beats
    // dropping a real conversion, so fall through and fire.
  }
  fn();
}
